import { parseFiniteNumber } from './_shared';

/**
 * Departures tab: how the user picked a stop. For `ext_id` the coord is
 * optional but written whenever we have it — round-tripping it means the
 * star-toggle after a URL restore has the payload it needs without a
 * second HAFAS lookup.
 */
export type DeparturesEndpoint =
  | { kind: 'ext_id'; extId: string; name?: string; latitude?: number; longitude?: number }
  | { kind: 'position'; latitude: number; longitude: number; name?: string }
  | { kind: 'query'; query: string };

/** All URL-derived state for the Departures tab. */
export interface DeparturesUrlState {
  endpoint: DeparturesEndpoint | undefined;
}

function extensionIdEndpoint(
  extensionId: string,
  name: string | undefined,
  latitude: number | undefined,
  longitude: number | undefined,
): DeparturesEndpoint {
  const endpoint: DeparturesEndpoint = { kind: 'ext_id', extId: extensionId, name };
  if (latitude != undefined) endpoint.latitude = latitude;
  if (longitude != undefined) endpoint.longitude = longitude;
  return endpoint;
}

/**
 * Reads Departures-tab state from URL params. Endpoint mode is picked by
 * whichever of `dep_ext_id`, `dep_lat`+`dep_lon`, or `dep_q` is populated,
 * in that priority order. Inverse of {@link encodeDeparturesUrl}.
 */
export function parseDeparturesUrl(parameters: URLSearchParams): DeparturesUrlState {
  const name = parameters.get('dep_name') ?? undefined;
  const latitude = parseFiniteNumber(parameters.get('dep_lat'));
  const longitude = parseFiniteNumber(parameters.get('dep_lon'));
  const extensionId = parameters.get('dep_ext_id');
  if (extensionId) {
    return { endpoint: extensionIdEndpoint(extensionId, name, latitude, longitude) };
  }
  if (latitude != undefined && longitude != undefined) {
    return { endpoint: { kind: 'position', latitude, longitude, name } };
  }
  const query = parameters.get('dep_q');
  if (query) return { endpoint: { kind: 'query', query } };
  return { endpoint: undefined };
}

function encodeExtensionIdEndpoint(
  output: URLSearchParams,
  endpoint: Extract<DeparturesEndpoint, { kind: 'ext_id' }>,
): void {
  output.set('dep_ext_id', endpoint.extId);
  if (endpoint.name) output.set('dep_name', endpoint.name);
  if (endpoint.latitude != undefined) output.set('dep_lat', String(endpoint.latitude));
  if (endpoint.longitude != undefined) output.set('dep_lon', String(endpoint.longitude));
}

function encodePositionEndpoint(
  output: URLSearchParams,
  endpoint: Extract<DeparturesEndpoint, { kind: 'position' }>,
): void {
  output.set('dep_lat', String(endpoint.latitude));
  output.set('dep_lon', String(endpoint.longitude));
  if (endpoint.name) output.set('dep_name', endpoint.name);
}

/** Serializes Departures-tab state into fresh URL params. Inverse of {@link parseDeparturesUrl}. */
export function encodeDeparturesUrl(state: DeparturesUrlState): URLSearchParams {
  const output = new URLSearchParams();
  const endpoint = state.endpoint;
  if (!endpoint) return output;
  if (endpoint.kind === 'ext_id') encodeExtensionIdEndpoint(output, endpoint);
  else if (endpoint.kind === 'position') encodePositionEndpoint(output, endpoint);
  else output.set('dep_q', endpoint.query);
  return output;
}
